import { useState, useMemo } from 'react';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Network } from 'lucide-react';
import { Role } from '@momentum/types';
import type { EmployeeHierarchyNode } from '@momentum/types';
import { useEmployeeContext } from '@/shared/contexts/EmployeeContext';
import { useCompanies } from '@/shared/hooks/useCompanies';
import { useCompanyEmployeeHierarchy } from '@/shared/hooks/useEmployees';
import { TreeView, type TreeDataItem } from '@/shared/components/ui/TreeView';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/Select';
import { Label } from '@/shared/components/ui/Label';
import { PageHeader } from '@/shared/components/PageHeader';

export const Route = createFileRoute('/_dashboard/admin/employee-hierarchy')({
  component: EmployeeHierarchyPage,
});

const ROLE_TRANSLATION_KEYS: Record<string, string> = {
  [Role.system_admin]: 'employeeHierarchyRoleSystemAdmin',
  [Role.admin]: 'employeeHierarchyRoleAdmin',
  [Role.manager]: 'employeeHierarchyRoleManager',
  [Role.employee]: 'employeeHierarchyRoleEmployee',
};

function transformToTreeData(
  nodes: EmployeeHierarchyNode[],
  translateKey: (key: string) => string
): TreeDataItem[] {
  return nodes.map((node) => {
    const roleKey = ROLE_TRANSLATION_KEYS[node.role];
    const roleLabel = roleKey != null ? translateKey(roleKey) : node.role;
    return {
      id: node.id,
      name: `${node.personalInfo.firstName} ${
        node.personalInfo.lastName
      } • ${roleLabel}${node.teamName ? ` • ${node.teamName}` : ''}`,
      children:
        node.children?.length > 0
          ? transformToTreeData(node.children, translateKey)
          : undefined,
    };
  });
}

function EmployeeHierarchyPage() {
  const { t } = useTranslation('common');
  const employee = useEmployeeContext();

  if (employee.role !== Role.system_admin) {
    return <Navigate to="/" />;
  }

  return (
    <div className="min-h-screen">
      <PageHeader title={t('employeeHierarchyPageTitle')} icon={Network} />

      <div className="mx-4 py-6 sm:mx-6 lg:mx-8">
        <EmployeeHierarchyContent />
      </div>
    </div>
  );
}

function EmployeeHierarchyContent() {
  const { t } = useTranslation('common');
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(
    null
  );

  const { data: companies = [], isLoading: companiesLoading } = useCompanies(
    { isActive: true },
    true
  );

  const { data: hierarchy = [], isLoading: hierarchyLoading } =
    useCompanyEmployeeHierarchy(selectedCompanyId);

  const translateKey = (key: string) =>
    t(
      key as
        | 'employeeHierarchyRoleSystemAdmin'
        | 'employeeHierarchyRoleAdmin'
        | 'employeeHierarchyRoleManager'
        | 'employeeHierarchyRoleEmployee'
    );
  const treeData = useMemo(
    () =>
      hierarchy.length > 0 ? transformToTreeData(hierarchy, translateKey) : [],
    [hierarchy, t]
  );

  return (
    <div className="space-y-6">
      <div className="space-y-2 max-w-md">
        <Label htmlFor="company-select">{t('employeeHierarchyCompany')}</Label>
        <Select
          value={selectedCompanyId ?? ''}
          onValueChange={(value) => setSelectedCompanyId(value || null)}
          disabled={companiesLoading}
        >
          <SelectTrigger id="company-select">
            <SelectValue placeholder={t('employeeHierarchySelectCompany')} />
          </SelectTrigger>
          <SelectContent>
            {companies.map((company) => (
              <SelectItem key={company._id} value={company._id}>
                {company.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!selectedCompanyId && (
        <p className="text-muted-foreground text-sm">
          {t('employeeHierarchySelectCompanyHint')}
        </p>
      )}

      {selectedCompanyId && hierarchyLoading && (
        <p className="text-muted-foreground text-sm">
          {t('employeeHierarchyLoading')}
        </p>
      )}

      {selectedCompanyId && !hierarchyLoading && treeData.length === 0 && (
        <p className="text-muted-foreground text-sm">
          {t('employeeHierarchyNoEmployees')}
        </p>
      )}

      {selectedCompanyId && !hierarchyLoading && treeData.length > 0 && (
        <TreeView data={treeData} expandAll={false} className="mt-6" />
      )}
    </div>
  );
}
